import React, { useState, useEffect } from "react";
import FormRow from "../../ui/FormRow";
import { useForm } from "react-hook-form";
import { useLogin } from "./useLogin";
import { Link, useNavigate } from "react-router-dom";
import { useCurrentUser } from "../authentication/useCurrentUser";
import SubPageLoader from "../../ui/SubPageLoader";

import PasswordToggle from "../../ui/PasswordToggle";
import { motion } from "framer-motion";
import { startOAuth } from "../../services/apiOAuth";
import SocialAuthButtons from "../../ui/SocialAuthButtons";
import Spinner from "../../ui/Spinner";

const LoginForm = () => {
  const navigate = useNavigate();
  const { login, isPending } = useLogin();
  const { isAuthenticated, isLoading } = useCurrentUser();
  const [showPassword, setShowPassword] = useState(false);
  const [socialProvider, setSocialProvider] = useState("");

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onChange",
  });

  const emailValue = watch("email");
  const passwordValue = watch("password");

  const isEmailValid =
    emailValue &&
    !errors.email &&
    /\S+@\S+\.\S+/.test(emailValue);

  const isPasswordValid = passwordValue && !errors.password && passwordValue.length > 0;

  const onSubmit = ({ email, password }) => {
    login({ email, password });
  };

  const onError = (err) => {
    console.log(err);
  };

  const handleSocialAuth = (provider) => {
    if (isPending || socialProvider) return;

    try {
      setSocialProvider(provider);
      startOAuth(provider);
    } catch (error) {
      console.error(error);
      setSocialProvider("");
    }
  };

  const isSocialLoading = Boolean(socialProvider);

  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      navigate("/dashboard", { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  if (isLoading) return <SubPageLoader />;
  if (isAuthenticated) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="glass-panel mx-auto w-full max-w-md rounded-2xl p-8 backdrop-blur-xl md:w-[450px]"
    >
      <div className="mb-8 text-center">
        <h2 className="text-3xl font-bold text-gray-800 dark:text-white">
          Welcome Back
        </h2>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          Please enter your details to sign in
        </p>
      </div>

      <SocialAuthButtons disabled={isPending || isSocialLoading} onSelect={handleSocialAuth} />

      <div className="mb-6 flex items-center justify-between">
        <span className="h-px w-full bg-gray-200 dark:bg-gray-700"></span>
        <span className="px-3 text-xs text-gray-400 uppercase">Or</span>
        <span className="h-px w-full bg-gray-200 dark:bg-gray-700"></span>
      </div>

      <form
        autoComplete="off"
        onSubmit={handleSubmit(onSubmit, onError)}
        className="space-y-5"
      >
        <FormRow
          label="Email"
          error={errors?.email?.message}
          isValid={isEmailValid}
        >
          <input
            type="email"
            placeholder="Enter your email"
            autoComplete="email"
            className={`w-full rounded-lg border bg-gray-50 p-4 text-sm outline-none transition-all focus:ring-2 dark:bg-[#0f172a] dark:text-white ${
              errors?.email?.message
                ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                : "border-gray-200 focus:border-[#6366f1] focus:ring-[#6366f1]/20 dark:border-gray-700"
            }`}
            {...register("email", {
              required: "Email is required",
              pattern: {
                value: /\S+@\S+\.\S+/,
                message: "Please provide a valid email address",
              },
            })}
            disabled={isPending}
          />
        </FormRow>

        <FormRow
          label="Password"
          error={errors?.password?.message}
          isValid={isPasswordValid}
        >
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Enter your password"
              autoComplete="current-password"
              className={`w-full rounded-lg border bg-gray-50 p-4 pr-12 text-sm outline-none transition-all focus:ring-2 dark:bg-[#0f172a] dark:text-white ${
                errors?.password?.message
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                  : "border-gray-200 focus:border-[#6366f1] focus:ring-[#6366f1]/20 dark:border-gray-700"
              }`}
              {...register("password", {
                required: "Password is required",
                minLength: {
                  value: 1,
                  message: "Password cannot be empty",
                },
              })}
              disabled={isPending}
            />
            <PasswordToggle
              show={showPassword}
              onToggle={() => setShowPassword(!showPassword)}
            />
          </div>
        </FormRow>

        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" className="rounded border-gray-300 text-[#6366f1] focus:ring-[#6366f1]" />
            <span className="text-xs text-gray-500 dark:text-gray-400">Remember me</span>
          </label>
          <Link
            className="text-xs font-medium text-[#6366f1] hover:text-[#818cf8] transition-colors"
            to={"/forgot-password"}
          >
            Forgot Password?
          </Link>
        </div>

        <button
          disabled={isPending}
          type="submit"
          className="w-full rounded-lg bg-[#6366f1] py-3.5 text-sm font-semibold text-white shadow-lg shadow-[#6366f1]/30 transition-all hover:-translate-y-1 hover:shadow-xl hover:shadow-[#6366f1]/40 disabled:cursor-not-allowed disabled:opacity-70"
        >
          {isPending ? (
            <span className="flex items-center justify-center gap-2">
              <Spinner />
              Signing in...
            </span>
          ) : (
            "Sign In"
          )}
        </button>
      </form>

      <p className="mt-8 text-center text-sm text-gray-500 dark:text-gray-400">
        Don't have an account?{" "}
        <Link
          className="font-medium text-[#6366f1] hover:text-[#818cf8] hover:underline transition-colors"
          to={"/register"}
        >
          Create an account
        </Link>
      </p>
    </motion.div>
  );
};

export default LoginForm;
